import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  fetchPresentationWorkspace,
  fetchPresentation,
  savePresentation,
  convertPresentationToEstimate,
  saveProductCatalogItem,
  type PresentationOption,
  type PresentationMeasurement,
  type SavePresentationInput,
} from '../lib/bridge';
import { LoadingState } from '../components/ui/LoadingState';
import { ErrorState } from '../components/ui/ErrorState';
import { EmptyState } from '../components/ui/EmptyState';
import { Button } from '../components/ui/Button';
import { Card, CardHeader } from '../components/ui/Card';
import { fieldControlClass, fieldControlSmClass, fieldLabelClass } from '../components/ui/forms';

function emptyForm(): SavePresentationInput {
  return { customerName: '', project: '', presentationTitle: '', serviceAddress: '', phone: '', email: '', notes: '', options: [], measurements: [] };
}

function money(n: number) {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

// Presentation builder — package/option selection from the Price Book or
// Product Catalog, room measurements, and Convert to Estimate. Every save
// goes through the unmodified appSavePresentation; converting goes through
// the unmodified appPresentationToEstimate (which itself calls the same
// appSaveEstimateDraft the Estimate builder uses).
export function PresentationBuilderPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const presentationId = params.get('presentationId') || '';
  const editing = !!presentationId;

  const workspaceQuery = useQuery({ queryKey: ['presentationWorkspace'], queryFn: fetchPresentationWorkspace });
  const loadQuery = useQuery({ queryKey: ['presentation', presentationId], queryFn: () => fetchPresentation(presentationId), enabled: editing });

  const [form, setForm] = useState<SavePresentationInput>(emptyForm());
  const [loaded, setLoaded] = useState(false);
  const [catalogPick, setCatalogPick] = useState('');
  const [pricePick, setPricePick] = useState('');
  const [saveMessage, setSaveMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (loaded) return;
    if (editing && loadQuery.data?.ok && loadQuery.data.presentation) {
      setForm({ ...emptyForm(), ...loadQuery.data.presentation, presentationId });
      setLoaded(true);
    } else if (!editing) {
      setLoaded(true);
    }
  }, [editing, loadQuery.data, presentationId, loaded]);

  const saveMutation = useMutation({
    mutationFn: () => savePresentation(form),
    onSuccess: (result) => {
      if (result.ok && result.presentation) {
        setForm((prev) => ({ ...prev, presentationId: result.presentation!.presentationId }));
        setSaveMessage({ kind: 'success', text: 'Saved.' });
        if (!editing) navigate(`/presentations/builder?presentationId=${encodeURIComponent(result.presentation.presentationId)}`, { replace: true });
      } else {
        setSaveMessage({ kind: 'error', text: result.message || 'Could not save this presentation.' });
      }
    },
  });

  const convertMutation = useMutation({
    mutationFn: () => convertPresentationToEstimate(form.presentationId || presentationId),
  });

  const quickAddProductMutation = useMutation({
    mutationFn: (name: string) => saveProductCatalogItem({ productName: name, category: 'Custom', customerPrice: 0, active: true }),
    onSuccess: () => void workspaceQuery.refetch(),
  });

  const selectedTotal = form.options.filter((o) => o.selected).reduce((n, o) => n + Number(o.qty || 0) * Number(o.unitPrice || 0), 0);

  function addOption(option: PresentationOption) {
    setForm((prev) => ({ ...prev, options: [...prev.options, option] }));
  }

  function updateOption(i: number, patch: Partial<PresentationOption>) {
    setForm((prev) => ({ ...prev, options: prev.options.map((o, idx) => (idx === i ? { ...o, ...patch } : o)) }));
  }

  function removeOption(i: number) {
    setForm((prev) => ({ ...prev, options: prev.options.filter((_, idx) => idx !== i) }));
  }

  function addMeasurement() {
    setForm((prev) => ({ ...prev, measurements: [...prev.measurements, { name: 'Room', length: 0, width: 0, height: 8 }] }));
  }

  function updateMeasurement(i: number, patch: Partial<PresentationMeasurement>) {
    setForm((prev) => ({ ...prev, measurements: prev.measurements.map((m, idx) => (idx === i ? { ...m, ...patch } : m)) }));
  }

  function removeMeasurement(i: number) {
    setForm((prev) => ({ ...prev, measurements: prev.measurements.filter((_, idx) => idx !== i) }));
  }

  if (workspaceQuery.isLoading || (editing && loadQuery.isLoading)) return <LoadingState label="Loading presentation…" />;
  if (workspaceQuery.isError || !workspaceQuery.data?.ok) {
    return <ErrorState message={workspaceQuery.data?.message || 'Could not load the presentation workspace.'} onRetry={() => workspaceQuery.refetch()} />;
  }
  if (editing && loadQuery.data && !loadQuery.data.ok) {
    return <ErrorState message={loadQuery.data.message || 'Could not load this presentation.'} />;
  }

  const products = workspaceQuery.data.products ?? [];
  const priceBook = (workspaceQuery.data.priceBook ?? []).filter((p) => p.active);

  return (
    <div className="space-y-5 pb-10">
      <div>
        <Link to="/presentations" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold-300 hover:text-gold-200">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Presentations
        </Link>
        <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-white">{editing ? 'Edit Presentation' : 'New Presentation'}</h1>
      </div>

      <Card>
        <CardHeader title="Customer &amp; Project" />
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            { id: 'pf-name', label: 'Customer Name', key: 'customerName' as const, required: true },
            { id: 'pf-project', label: 'Project', key: 'project' as const, required: true },
            { id: 'pf-title', label: 'Presentation Title', key: 'presentationTitle' as const },
            { id: 'pf-address', label: 'Service Address', key: 'serviceAddress' as const },
            { id: 'pf-phone', label: 'Phone', key: 'phone' as const },
            { id: 'pf-email', label: 'Email', key: 'email' as const },
          ].map((f) => (
            <div key={f.id} className="space-y-1">
              <label htmlFor={f.id} className={fieldLabelClass}>
                {f.label}
              </label>
              <input
                id={f.id}
                className={fieldControlClass}
                value={(form[f.key] as string) || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                required={f.required}
              />
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <CardHeader title="Package Options" />
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[12rem] flex-1 space-y-1">
            <label htmlFor="pb-catalog" className={fieldLabelClass}>
              Add from Product Catalog
            </label>
            <select id="pb-catalog" className={fieldControlClass} value={catalogPick} onChange={(e) => setCatalogPick(e.target.value)}>
              <option value="">Select a product…</option>
              {products.filter((p) => p.active).map((p) => (
                <option key={p.productId} value={p.productId}>
                  {p.brand} {p.productName} ({money(p.customerPrice)})
                </option>
              ))}
            </select>
          </div>
          <Button
            type="button"
            variant="secondary"
            disabled={!catalogPick}
            onClick={() => {
              const product = products.find((p) => p.productId === catalogPick);
              if (!product) return;
              addOption({ productId: product.productId, category: product.category, type: 'Product', title: `${product.brand} ${product.productName}`.trim(), description: product.description, qty: 1, unit: 'item', unitPrice: product.customerPrice, selected: false, imageUrl: product.imageUrl });
              setCatalogPick('');
            }}
          >
            Add
          </Button>
        </div>

        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="min-w-[12rem] flex-1 space-y-1">
            <label htmlFor="pb-pricebook" className={fieldLabelClass}>
              Add from Price Book
            </label>
            <select id="pb-pricebook" className={fieldControlClass} value={pricePick} onChange={(e) => setPricePick(e.target.value)}>
              <option value="">Select a service…</option>
              {priceBook.map((p) => (
                <option key={p.serviceId} value={p.serviceId}>
                  {p.category} — {p.name} ({p.currentPriceText})
                </option>
              ))}
            </select>
          </div>
          <Button
            type="button"
            variant="secondary"
            disabled={!pricePick}
            onClick={() => {
              const item = priceBook.find((p) => p.serviceId === pricePick);
              if (!item) return;
              addOption({ serviceId: item.serviceId, category: item.category, type: 'Service', title: item.name, description: item.description, qty: 1, unit: item.unit, unitPrice: item.currentPrice, selected: false });
              setPricePick('');
            }}
          >
            Add
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => addOption({ category: 'Custom', type: 'Service', title: 'Custom Option', qty: 1, unit: 'item', unitPrice: 0, selected: false })}
          >
            Add Custom
          </Button>
        </div>

        {form.options.length === 0 && <EmptyState className="mt-3">No options yet.</EmptyState>}
        {form.options.length > 0 && (
          <ul className="mt-3 space-y-2">
            {form.options.map((o, i) => (
              <li key={i} className="flex flex-wrap items-end gap-2 rounded-lg border border-ink-border bg-ink-raised p-2.5">
                <label className="flex items-center gap-1.5 text-xs text-slate-300">
                  <input type="checkbox" checked={o.selected} onChange={(e) => updateOption(i, { selected: e.target.checked })} />
                  Selected
                </label>
                <div className="min-w-[8rem] flex-1 space-y-1">
                  <label className="text-[10px] font-semibold uppercase text-slate-500">Title</label>
                  <input className={fieldControlSmClass} value={o.title} onChange={(e) => updateOption(i, { title: e.target.value })} />
                </div>
                <div className="w-16 space-y-1">
                  <label className="text-[10px] font-semibold uppercase text-slate-500">Qty</label>
                  <input type="number" min="0" step="0.01" className={fieldControlSmClass} value={o.qty} onChange={(e) => updateOption(i, { qty: Number(e.target.value) })} />
                </div>
                <div className="w-24 space-y-1">
                  <label className="text-[10px] font-semibold uppercase text-slate-500">Unit Price</label>
                  <input type="number" min="0" step="0.01" className={fieldControlSmClass} value={o.unitPrice} onChange={(e) => updateOption(i, { unitPrice: Number(e.target.value) })} />
                </div>
                <p className="w-20 text-right text-sm font-semibold text-gold-200">{money(Number(o.qty || 0) * Number(o.unitPrice || 0))}</p>
                <button type="button" onClick={() => removeOption(i)} className="text-xs font-semibold text-red-300 hover:text-red-200">
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        <p className="mt-4 border-t border-ink-border pt-3 text-sm font-bold text-gold-200">Selected Total: {money(selectedTotal)}</p>
      </Card>

      <Card>
        <CardHeader title="Room Measurements" action={<Button type="button" variant="secondary" onClick={addMeasurement}>Add Room</Button>} />
        {form.measurements.length === 0 && <EmptyState>No rooms measured yet.</EmptyState>}
        {form.measurements.length > 0 && (
          <ul className="space-y-2">
            {form.measurements.map((m, i) => (
              <li key={i} className="flex flex-wrap items-end gap-2 rounded-lg border border-ink-border bg-ink-raised p-2.5">
                <div className="min-w-[7rem] flex-1 space-y-1">
                  <label className="text-[10px] font-semibold uppercase text-slate-500">Name</label>
                  <input className={fieldControlSmClass} value={m.name} onChange={(e) => updateMeasurement(i, { name: e.target.value })} />
                </div>
                {(['length', 'width', 'height'] as const).map((dim) => (
                  <div key={dim} className="w-20 space-y-1">
                    <label className="text-[10px] font-semibold uppercase capitalize text-slate-500">{dim} (ft)</label>
                    <input type="number" min="0" step="0.1" className={fieldControlSmClass} value={m[dim]} onChange={(e) => updateMeasurement(i, { [dim]: Number(e.target.value) })} />
                  </div>
                ))}
                <button type="button" onClick={() => removeMeasurement(i)} className="text-xs font-semibold text-red-300 hover:text-red-200">
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {saveMessage && (
        <p className={`rounded-xl border p-3.5 text-sm ${saveMessage.kind === 'success' ? 'border-success/30 bg-success-soft text-emerald-200' : 'border-danger/30 bg-danger-soft text-red-200'}`}>
          {saveMessage.text}
        </p>
      )}
      {convertMutation.data?.ok && (
        <p className="rounded-xl border border-success/30 bg-success-soft p-3.5 text-sm text-emerald-200">
          Converted to estimate draft {convertMutation.data.draftId} ({convertMutation.data.totalText}).{' '}
          <Link to="/estimates" className="font-semibold underline">
            View drafts
          </Link>
        </p>
      )}
      {convertMutation.data && !convertMutation.data.ok && <ErrorState message={convertMutation.data.message || 'Could not convert this presentation to an estimate.'} />}

      <div className="flex flex-wrap gap-2">
        <Button busy={saveMutation.isPending} busyLabel="Saving…" disabled={!form.customerName.trim() || !form.project.trim()} onClick={() => saveMutation.mutate()}>
          Save
        </Button>
        {editing && (
          <Button
            variant="secondary"
            busy={convertMutation.isPending}
            busyLabel="Converting…"
            disabled={!form.options.some((o) => o.selected)}
            onClick={() => convertMutation.mutate()}
          >
            Convert to Estimate
          </Button>
        )}
      </div>
      {/* Quick-add a not-yet-cataloged product, for canEditPrices users; a
          fuller product-catalog editor is a reasonable future addition, not
          required for this workflow's round trip. */}
      <button
        type="button"
        onClick={() => {
          const name = window.prompt('New product name?');
          if (name && name.trim()) quickAddProductMutation.mutate(name.trim());
        }}
        className="text-xs font-semibold text-slate-500 hover:text-slate-300"
      >
        + Quick-add a product to the catalog
      </button>
    </div>
  );
}
